import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  acceptOrganizationApi,
  changeOrganizationApi,
  createOrgApi,
  declineOrganizationApi,
  deleteMemberApi,
  fetchCurrentOrganization,
  getOrganizationMembersApi,
  getOrganizationsListApi,
  inviteMembersApi,
  joinOrgApi,
  leaveOrgApi,
  softDeleteInviteByInviteeApi,
  softDeleteInviteByInviterApi,
  updateOrganizationNameApi,
} from '~/api/organization';
import type { Organization, OrganizationsMember } from '~/types/organization';

export const useOrganizationStore = defineStore('organization', () => {
  const profileStore = useProfileStore();
  const currentOrganization = ref<OrganizationsMember | null>(null);
  const organizationsList = ref<Organization[]>([]);
  const members = ref<OrganizationsMember[]>([]);
  const loading = ref(false);
  const currentOrganizationPromise = ref<Promise<OrganizationsMember> | null>(null);
  const lastFetchFailedAt = ref<number | null>(null);
  const lastFetchError = ref<unknown>(null);
  const FETCH_ERROR_COOLDOWN_MS = 3000;
  // computed
  const isAdmin = computed(() => currentOrganization.value?.is_admin ?? false);
  const organizationName = computed(() => currentOrganization.value?.organizations_name);
  const organizationUuid = computed(() => currentOrganization.value?.organizations_uuid);

  // actions
  async function getCurrentOrganization(options?: { force?: boolean }) {
    if (!options?.force && currentOrganization.value) {
      return currentOrganization.value;
    }

    if (!options?.force && currentOrganizationPromise.value) {
      return await currentOrganizationPromise.value;
    }

    if (
      !options?.force &&
      lastFetchFailedAt.value &&
      Date.now() - lastFetchFailedAt.value < FETCH_ERROR_COOLDOWN_MS
    ) {
      throw lastFetchError.value ?? new Error('Failed to fetch current organization recently');
    }

    currentOrganizationPromise.value = fetchCurrentOrganization()
      .then((org) => {
        currentOrganization.value = org;
        lastFetchFailedAt.value = null;
        lastFetchError.value = null;
        return org;
      })
      .catch((error) => {
        lastFetchFailedAt.value = Date.now();
        lastFetchError.value = error;
        throw error;
      })
      .finally(() => {
        currentOrganizationPromise.value = null;
      });

    return await currentOrganizationPromise.value;
  }

  async function joinOrganization(organizationUuid: string) {
    return await joinOrgApi(organizationUuid);
  }

  async function createOrganization(name: string) {
    currentOrganization.value = await createOrgApi(name);

    // 創建組織後自動刷新 token
    try {
      const auth = useAuth();
      await auth.refreshTokenAfterOrgSwitch();
    } catch (error) {
      console.warn('Failed to refresh token after organization creation:', error);
      // 如果刷新 token 失敗，不阻擋組織創建流程
    }

    return currentOrganization.value;
  }

  function clearOrganization() {
    currentOrganization.value = null;
    currentOrganizationPromise.value = null;
    lastFetchFailedAt.value = null;
    lastFetchError.value = null;
  }

  async function fetchOrganizationsList() {
    organizationsList.value = await getOrganizationsListApi();
    return organizationsList.value;
  }

  async function getOrganizationsList() {
    if (organizationsList.value.length > 0) {
      return organizationsList.value;
    }
    organizationsList.value = await fetchOrganizationsList();
    return organizationsList.value;
  }

  async function setCurrentOrganization(organizationUuid: string) {
    // 切換組織 API 現在返回認證令牌資訊
    const authResponse = await changeOrganizationApi(organizationUuid);

    // 更新認證令牌
    try {
      const authStore = useAuthStore();
      authStore.setTokens(authResponse.token, authResponse.refresh_token);
    } catch (error) {
      console.warn('Failed to update tokens after organization switch:', error);
      // 如果更新 token 失敗，不阻擋組織切換流程
    }

    // 重新獲取當前組織資訊
    try {
      currentOrganization.value = await fetchCurrentOrganization();
    } catch (error) {
      console.warn('Failed to fetch current organization after switch:', error);
      // 如果獲取組織資訊失敗，不阻擋組織切換流程
    }

    return currentOrganization.value;
  }

  async function leaveOrganization(organizationUuid: string) {
    const authResponse = await leaveOrgApi(organizationUuid);

    try {
      const authStore = useAuthStore();
      authStore.setTokens(authResponse.token, authResponse.refresh_token);
    } catch (error) {
      console.warn('Failed to update tokens after leaving organization:', error);
    }

    organizationsList.value = organizationsList.value.filter(
      (organization) => organization.organizations_uuid !== organizationUuid
    );

    const leftCurrentOrganization =
      currentOrganization.value?.organizations_uuid === organizationUuid;
    if (leftCurrentOrganization) {
      clearOrganization();
      members.value = [];
    }

    return { organizationsList: organizationsList.value, leftCurrentOrganization };
  }

  async function getOrganizationMembers() {
    if (members.value.length > 0) {
      return members.value;
    }
    members.value = await getOrganizationMembersApi();
    return members.value;
  }

  async function fetchOrganizationMembers() {
    members.value = await getOrganizationMembersApi();
    return members.value;
  }

  async function updateOrganizationName(organizationUuid: string, name: string) {
    const response = await updateOrganizationNameApi(organizationUuid, name);
    if (currentOrganization.value) {
      currentOrganization.value.organizations_name = name;
    }
    organizationsList.value = organizationsList.value.map((organization) => {
      if (organization.organizations_uuid === organizationUuid) {
        organization.name = name;
      }
      return organization;
    });
    return response;
  }

  async function deleteMember(member: OrganizationsMember) {
    await deleteMemberApi({ uid: member.uid, id: member.id });
    members.value = members.value.filter((item) => {
      if (member.uid && member.uid !== 0) {
        return item.uid !== member.uid;
      }
      return item.id !== member.id;
    });
  }

  async function inviteMembers(emails: string[]) {
    await inviteMembersApi(emails);
  }

  async function acceptOrganization(organizationToken: string) {
    await acceptOrganizationApi(organizationToken);
  }

  async function declineOrganization(organizationUuid: string) {
    await declineOrganizationApi(organizationUuid);
  }

  /**
   * 邀請方軟刪除邀請
   * @param member 要刪除的成員
   */
  async function softDeleteInviteByInviter(member: OrganizationsMember) {
    // 如果 uid 為 0（用戶尚未註冊），則使用 id
    const targetUid = member.uid !== 0 ? member.uid : member.id;
    await softDeleteInviteByInviterApi(member.organizations_uuid, targetUid);
    // 從本地列表中移除該成員
    members.value = members.value.filter((item) => item.id !== member.id);
  }

  /**
   * 被邀請方軟刪除邀請（在被邀請方的組織列表中調用）
   * @param organizationUuid 組織 UUID
   */
  async function softDeleteInviteByInvitee(organizationUuid: string) {
    await softDeleteInviteByInviteeApi(organizationUuid);
    // 從組織列表中移除該組織
    organizationsList.value = organizationsList.value.filter(
      (org) => org.organizations_uuid !== organizationUuid
    );
  }

  /**
   * 重新邀請成員（適用於 rejected/expired 狀態）
   * @param email 成員的電子郵件
   */
  async function reinviteMember(email: string) {
    await inviteMembersApi([email]);
    // 重新獲取成員列表
    await fetchOrganizationMembers();
  }

  const reset = () => {
    clearOrganization();
    organizationsList.value = [];
    members.value = [];
    loading.value = false;
  };

  return {
    currentOrganization,
    isAdmin,
    organizationName,
    organizationUuid,
    organizationsList,
    members,
    loading,
    getCurrentOrganization,
    setCurrentOrganization,
    joinOrganization,
    createOrganization,
    clearOrganization,
    getOrganizationsList,
    fetchOrganizationsList,
    leaveOrganization,
    getOrganizationMembers,
    fetchOrganizationMembers,
    updateOrganizationName,
    deleteMember,
    inviteMembers,
    acceptOrganization,
    declineOrganization,
    softDeleteInviteByInviter,
    softDeleteInviteByInvitee,
    reinviteMember,
    reset,
  };
});
